class SiteHeader {
  constructor(el) {
    this.el = el;
    this.menuBtn = el.querySelector('[data-menu-open]');
    this.cartBtn = el.querySelector('[data-cart-open]');
    this.cartLabelEl = el.querySelector('.site-header__cart-label');
    this.cartCountEl = el.querySelector('.site-header__cart-count');
    this.logo = el.querySelector('[data-logo-hero]');

    this.isRevealed = false;

    this.initScrollReveal();
    this.initLogoScrollAnimation();
    this.initBackButton();
    this.bindCartEvents();
    this.bindLightboxEvents();
  }

  initScrollReveal() {
    if (!this.logo) return;

    const isInternalPage = this.el.classList.contains('site-header--scroll-logo');
    const INTERNAL_TOP_ZONE = 10;
    const TOP_ZONE = 100;
    let lastScrollY = window.scrollY;
    let upScrollDistance = 0;
    const THRESHOLD = 10;

    const revealLogo = () => {
      if (this.isRevealed) return;
      this.isRevealed = true;
      this.el.classList.remove('logo-hiding');
      this.el.classList.add('logo-revealed');
    };

    const hideLogo = () => {
      if (!this.isRevealed) return;
      this.isRevealed = false;
      this.el.classList.remove('logo-revealed');
      this.el.classList.add('logo-hiding');
    };

    const onScroll = () => {
      if (document.body.classList.contains('is-scroll-locked')) return;

      const scrollY = window.scrollY;
      const isScrollUp = scrollY < lastScrollY;

      if (isScrollUp) {
        upScrollDistance += lastScrollY - scrollY;
      } else {
        upScrollDistance = 0;
      }

      // Internal pages: fixed fade logo only — visible within 10px of top, or on scroll-up below
      if (isInternalPage) {
        if (scrollY <= INTERNAL_TOP_ZONE) {
          revealLogo();
        } else if (upScrollDistance >= THRESHOLD) {
          revealLogo();
        } else if (!isScrollUp) {
          hideLogo();
        }

        lastScrollY = scrollY;
        return;
      }

      const isHomeDesktop =
        document.body.dataset.template === 'index' && !window.matchMedia('(max-width: 768px)').matches;
      const thresholdY = isHomeDesktop ? Math.round(window.innerHeight * 0.5) : TOP_ZONE;

      if (scrollY > thresholdY) {
        if (upScrollDistance >= THRESHOLD && !this.isRevealed) {
          revealLogo();
        } else if (!isScrollUp && this.isRevealed) {
          hideLogo();
        }
      } else if (this.isRevealed || this.el.classList.contains('logo-hiding')) {
        this.isRevealed = false;
        this.el.classList.remove('logo-revealed');
        this.el.classList.remove('logo-hiding');
      }

      lastScrollY = scrollY;
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ─── Logo scroll animation (homepage only) ───────────────────────────────
  //
  // Fixed header (no layout gap). At scroll=0 the logo is centred in the viewport
  // at type-h1 scale. Over SCROLL_DISTANCE it animates to the header row; beyond
  // that, translateY moves it off-screen while menu/cart stay fixed.

  initLogoScrollAnimation() {
    if (document.body.dataset.template !== 'index') return;

    const logo = this.logo;
    if (!logo) return;

    if (window.matchMedia('(max-width: 768px)').matches) {
      this.initLogoMobileScrollOff(logo);
      return;
    }

    const cached = window.__hattieLogoHero;
    let translateYStart = cached?.translateYStart;
    let h1Size = cached?.h1Size;
    let h2Size = cached?.h2Size;

    if (translateYStart == null || h1Size == null || h2Size == null) {
      const probe = document.createElement('span');
      probe.className = 'type-h1';
      probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;white-space:nowrap;';
      probe.textContent = 'HATTIE';
      document.body.appendChild(probe);
      h1Size = parseFloat(getComputedStyle(probe).fontSize);
      document.body.removeChild(probe);

      h2Size = parseFloat(getComputedStyle(logo).fontSize);
      if (!(h1Size > 0) || !(h2Size > 0)) return;

      const logoRect = logo.getBoundingClientRect();
      const logoCenterY = logoRect.top + logoRect.height / 2;
      translateYStart = window.innerHeight / 2 - logoCenterY;
    }

    const SCROLL_DISTANCE = Math.round(window.innerHeight * 0.5);

    logo.style.willChange = 'transform, font-size';

    let rafId = null;

    const update = () => {
      if (document.body.classList.contains('is-scroll-locked')) return;

      const scrollY = window.scrollY;
      const progress = Math.min(scrollY / SCROLL_DISTANCE, 1);
      let ty;
      let fontSize;

      if (scrollY <= SCROLL_DISTANCE) {
        ty = translateYStart * (1 - progress);
        fontSize = h1Size - (h1Size - h2Size) * progress;
        logo.style.fontSize = `${fontSize}px`;
        logo.style.transform = `translateY(${ty}px)`;
      } else {
        ty = -(scrollY - SCROLL_DISTANCE);
        logo.style.fontSize = `${h2Size}px`;
        logo.style.transform = `translateY(${ty}px)`;
      }
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // Homepage mobile: logo is fixed in the header row — translate it off-screen on scroll.
  initLogoMobileScrollOff(logo) {
    logo.style.willChange = 'transform';

    let rafId = null;

    const update = () => {
      if (document.body.classList.contains('is-scroll-locked')) return;

      const scrollY = window.scrollY;
      if (scrollY <= 0) {
        logo.style.transform = '';
        return;
      }
      logo.style.transform = `translateY(${-scrollY}px)`;
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ─── Back button (product pages referred from a collection) ──────────────
  //
  // Show ← Back only when the visitor arrived from a same-origin collection
  // listing (not Google, social, or other external referrers). Stale
  // sessionStorage from an earlier visit is ignored when referrer is external.

  static FROM_COLLECTION_KEY = 'from-collection';

  static isSameOriginUrl(urlString) {
    try {
      return new URL(urlString).origin === window.location.origin;
    } catch (_) {
      return false;
    }
  }

  static isCollectionListingPath(pathname) {
    // /collections/:handle — not /collections/:handle/products/:handle
    return /^\/collections\/[^/]+\/?$/.test(pathname);
  }

  static isCollectionListingUrl(urlString) {
    try {
      const url = new URL(urlString, window.location.origin);
      if (url.origin !== window.location.origin) return false;
      return SiteHeader.isCollectionListingPath(url.pathname);
    } catch (_) {
      return false;
    }
  }

  static getCollectionListingReferrer(referrer) {
    if (!referrer || !SiteHeader.isSameOriginUrl(referrer)) return null;
    try {
      const url = new URL(referrer);
      if (!SiteHeader.isCollectionListingPath(url.pathname)) return null;
      return referrer;
    } catch (_) {
      return null;
    }
  }

  initBackButton() {
    const key = SiteHeader.FROM_COLLECTION_KEY;
    const isProductPage = document.body.dataset.template === 'product';

    if (!isProductPage) {
      sessionStorage.removeItem(key);
      return;
    }

    const referrer = document.referrer;
    const fromReferrer = SiteHeader.getCollectionListingReferrer(referrer);

    if (fromReferrer) {
      sessionStorage.setItem(key, fromReferrer);
      this.replaceMenuWithBack();
      return;
    }

    // External referrer (Google, social, etc.) — always show Menu
    if (referrer && !SiteHeader.isSameOriginUrl(referrer)) {
      sessionStorage.removeItem(key);
      return;
    }

    // Same-origin but not a collection listing (home, cart, another product, etc.)
    if (referrer) {
      sessionStorage.removeItem(key);
      return;
    }

    // No referrer (direct entry, privacy mode) — trust storage only if set by a
    // collection → product click in this tab (see collection-grid.js).
    const stored = sessionStorage.getItem(key);
    if (stored && SiteHeader.isCollectionListingUrl(stored)) {
      this.replaceMenuWithBack();
      return;
    }

    sessionStorage.removeItem(key);
  }

  replaceMenuWithBack() {
    if (!this.menuBtn) return;

    const backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.className = 'site-header__back-btn type-h3';
    backBtn.setAttribute('aria-label', 'Go back to collection');
    backBtn.textContent = 'Close';
    backBtn.addEventListener('click', () => history.back());

    this.menuBtn.replaceWith(backBtn);
    this.menuBtn = null;
  }

  // ─── Cart button state ────────────────────────────────────────────────────

  bindCartEvents() {
    // cart-drawer.js dispatches 'hattie:cart-updated' with { itemCount } after
    // any cart mutation (add / change / remove).
    document.addEventListener('hattie:cart-updated', (e) => {
      const count = e.detail?.itemCount ?? 0;

      if (this.cartCountEl) {
        this.cartCountEl.textContent = `(${count})`;
      }

      if (this.cartBtn && this.cartBtn.getAttribute('aria-expanded') !== 'true') {
        this.cartBtn.setAttribute('aria-label', `Open cart, ${count} ${count === 1 ? 'item' : 'items'}`);
      }
    });

    // cart-drawer.js dispatches these when the drawer opens / closes.
    document.addEventListener('hattie:cart-opened', () => this.setCartBtnState(true));
    document.addEventListener('hattie:cart-closed', () => this.setCartBtnState(false));
  }

  setCartBtnState(isOpen) {
    if (!this.cartBtn) return;
    this.cartBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    const count = this._getCartItemCount();
    const countLabel = `${count} ${count === 1 ? 'item' : 'items'}`;
    this.cartBtn.setAttribute('aria-label', isOpen ? `Close cart, ${countLabel}` : `Open cart, ${countLabel}`);
  }

  _getCartItemCount() {
    const countEl = this.cartCountEl;
    if (!countEl) return 0;
    const match = countEl.textContent.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  }

  // ─── Lightbox (product-hero zoom) ────────────────────────────────────────
  //
  // product-carousel.js dispatches hattie:lightbox-opened / hattie:lightbox-closed
  // when the zoom lightbox opens or closes.

  bindLightboxEvents() {
    document.addEventListener('hattie:lightbox-opened', () => {
      this.el.hidden = true;
    });

    document.addEventListener('hattie:lightbox-closed', () => {
      this.el.hidden = false;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('.site-header');
  if (header) new SiteHeader(header);
});
